import { getPersonaQualityTier, PersonaQualityTier } from './persona_quality';
import type { Persona } from '../types/persona';

export interface TierPresentation {
  tier: PersonaQualityTier;
  glyph: string;          // ⚡ / ◐ / ○ — render aria-hidden; decorative
  label: string;          // Forged / Draft / Bare — bare word for composed labels
  ariaLabel: string;      // "<Label> persona" — sr-only badge text
  dotClass: string;       // status-dot bg, contrast-safe (+ dark:)
  chipClass: string;      // badge/pill bg+text+border (+ dark:)
  subtitleClass: string;  // subtitle/helper-text color (+ dark:)
  ringClass: string;      // tile border ring (+ dark:)
  shadowClass: string;    // resting shadow (+ dark:)
  hoverClass: string;     // hover shadow (+ dark:)
}

export const TIER_PRESENTATION: Record<PersonaQualityTier, Omit<TierPresentation, 'tier'>> = {
  forged: {
    glyph: '⚡',
    label: 'Forged',
    ariaLabel: 'Forged persona',
    dotClass: 'bg-emerald-600 dark:bg-emerald-500',
    chipClass: 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300',
    subtitleClass: 'text-gray-500 dark:text-gray-400',
    ringClass: 'border-2 border-emerald-500 dark:border-emerald-400',
    shadowClass: 'shadow-md shadow-emerald-500/30 dark:shadow-emerald-400/20',
    hoverClass: 'hover:shadow-lg hover:shadow-emerald-500/50 dark:hover:shadow-emerald-400/40',
  },
  draft: {
    glyph: '◐',
    label: 'Draft',
    ariaLabel: 'Draft persona',
    dotClass: 'bg-amber-600 dark:bg-amber-400',
    chipClass: 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300',
    subtitleClass: 'text-gray-500 dark:text-gray-400',
    // dark: amber-400 — amber-700 is invisible on bg-gray-800 (triad ux), and a Haiku
    // visual review found amber-500 (border-1) still washes out next to forged's bold
    // emerald-400 border-2; amber-400 (#fbbf24) is the legible floor (Alan 2026-06-21).
    ringClass: 'border border-amber-300 dark:border-amber-400',
    shadowClass: 'shadow-sm shadow-amber-300/20 dark:shadow-amber-500/20',
    hoverClass: 'hover:shadow-md hover:shadow-amber-300/40 dark:hover:shadow-amber-500/40',
  },
  bare: {
    glyph: '○',
    label: 'Bare',
    ariaLabel: 'Bare persona',
    dotClass: 'bg-gray-500 dark:bg-gray-400',
    chipClass: 'bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300',
    subtitleClass: 'text-amber-700 dark:text-amber-400',
    ringClass: 'border border-gray-200 dark:border-gray-700',
    shadowClass: 'shadow-sm',
    hoverClass: 'hover:shadow-md',
  },
};

export function getTierPresentation(p: Persona): TierPresentation {
  const tier = getPersonaQualityTier(p);
  return { tier, ...TIER_PRESENTATION[tier] };
}
